import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Drawer, Dropdown, Flex, Modal, Popconfirm, message } from "antd";
import { HistoryOutlined, DeleteOutlined, SendOutlined, PlusOutlined, MoreOutlined } from "@ant-design/icons";
import moment from "moment";

import AiApi from "../../api/AiApi";
import AiConversationApi from "../../api/AiConversationApi";
import MessageService from "../../services/message/MessageService";
import {
    AiAssistantWrapper,
    HeaderWrapper,
    HeaderTitle,
    ChatAreaWrapper,
    MessageWrapper,
    MessageBubble,
    MessageContent,
    MessageLoading,
    InputAreaWrapper,
    InputWrapper,
    TextArea,
    HistoryModalWrapper,
    HistoryListWrapper,
    HistoryItemWrapper,
    HistoryItemContent,
    HistoryItemTitle,
    HistoryItemTime,
    HistoryItemActions
} from "./AiAssistant.styled";
import Markdown from "../../components/markdown/Markdown";

interface AiAssistantProps {

}

const AiAssistant: React.FC<AiAssistantProps> = (props) => {
    // params
    const { conversationId } = useParams<{ conversationId?: string }>();

    // api
    const navigate = useNavigate();
    const aiApi = new AiApi();
    const conversationApi = new AiConversationApi();
    const [messageApi, contextHolder] = message.useMessage();

    // state
    let [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    let [messages, setMessages] = useState<any[]>([]);
    let [inputValue, setInputValue] = useState("");
    let [isSending, setIsSending] = useState(false);
    let [creatingConversation, setCreatingConversation] = useState(false);
    let [historyModalVisible, setHistoryModalVisible] = useState(false);
    let [conversations, setConversations] = useState<any[]>([]);
    let [loadingConversations, setLoadingConversations] = useState(false);

    // ref
    const chatAreaRef = useRef<HTMLDivElement>(null);
    const scrollToBottom = useCallback(() => {
        if (chatAreaRef.current) {
            chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
        }
    }, []);

    // options
    let faq = [
        {question: '查询在线/离线设备数量及清单'},
        {question: '查看设备实时状态、信号、末次上报时间'},
        {question: '统计近7天设备平均在线时长，展示在线 Top10 设备'},
        {question: '列出今日未处理告警，区分故障/预警告警'},
        {question: '统计近30天告警类型频次，展示高发异常'},
        {question: '筛选近24小时频繁掉线设备'},
    ];

    // effects
    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        if (conversationId && !creatingConversation) {
            setCurrentConversationId(conversationId);
            loadConversation(conversationId);
        }
    }, [conversationId]);

    // functions
    const createConversation = async (name: string) => {
        try {
            if (name.length >= 100) {
                name = name.substring(0, 100) + '...';
            }

            const conversation = (await conversationApi.create({
                name: name,
                remarks: '',
            })).data.data;

            const conversationId = conversation.id;
            setCurrentConversationId(conversationId);
            navigate(`/ai/chat/${conversationId}`);
            return conversation;
        } catch (error) {
            MessageService.getInstance().showMessage('error', '创建会话失败');
        }
    };

    const newConversation = async () => {
        setCurrentConversationId(null);
        setMessages([]);
        navigate(`/ai/chat`);
    };

    const loadConversation = async (conversationId: string) => {
        try {
            let conversation = (await conversationApi.get(conversationId)).data.data;
            let conversationMessages: any[] = [];
            if (conversation.messages) {
                let messages = JSON.parse(conversation.messages);
                conversationMessages = messages || [];
            }
            setMessages(conversationMessages);
        } catch (error) {
            MessageService.getInstance().showMessage('error', '加载会话失败');
        }
    };

    const loadConversations = async () => {
        setLoadingConversations(true);
        try {
            const conversations = (await conversationApi.getAll()).data.data;
            setConversations(conversations || []);
        } catch (error) {
            MessageService.getInstance().showMessage('error', '加载历史会话失败');
        } finally {
            setLoadingConversations(false);
        }
    };

    const handleSendFaq = async (question) => {
        setInputValue(question);
        inputValue = question;
        handleSend();
    };

    const handleSend = async () => {
        const trimmedMessage = inputValue.trim();
        if (!trimmedMessage || isSending) {
            return;
        }
        setIsSending(true);

        const userMessage = {
            conversationId: currentConversationId,
            text: trimmedMessage,
            metadata: {
                messageType: 'USER',
                timestamp: new Date().getTime() + 1,
            }
        };
        const newMessages = [...messages, userMessage];
        setMessages(newMessages);
        setInputValue("");

        const assistantMessage = {
            conversationId: currentConversationId,
            text: '',
            metadata: {
                messageType: 'ASSISTANT',
                timestamp: new Date().getTime(),
            }
        };
        newMessages.push(assistantMessage);
        setMessages(newMessages);
                
        let conversationId = currentConversationId;
        if (!conversationId) {
            setCreatingConversation(true);
            const conversation = await createConversation(inputValue);
            conversationId = conversation.id;
        }

        try {
            await aiApi.chatStream(
                conversationId,
                trimmedMessage,
                (content) => {
                    console.log(content);
                    assistantMessage.text += content;
                    setMessages([...newMessages]);
                },
                () => {
                    setIsSending(false);
                },
                (error) => {
                    setIsSending(false);
                    assistantMessage.text += '\r\n请求失败，请重试';
                    setMessages([...newMessages]);
                    MessageService.getInstance().showMessage('error', '请求失败');
                }
            );
        } catch (error) {
            setIsSending(false);
            assistantMessage.text += '\r\n请求失败，请重试';
            setMessages([...newMessages]);
            MessageService.getInstance().showMessage('error', '请求失败');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleOpenHistory = () => {
        loadConversations();
        setHistoryModalVisible(true);
    };

    const handleCloseHistory = () => {
        setHistoryModalVisible(false);
    };

    const handleSelectConversation = (conv) => {
        setHistoryModalVisible(false);
        setCreatingConversation(false);
        setCurrentConversationId(conv.id);
        loadConversation(conv.id);
        navigate(`/ai/chat/${conv.id}`);
    };

    const handleDeleteConversation = async (convId: string) => {
        try {
            await conversationApi.delete(convId);
            setConversations(prev => prev.filter(c => c.id !== convId));
            newConversation();
            MessageService.getInstance().showMessage('success', '删除成功');
        } catch (error) {
            MessageService.getInstance().showMessage('error', '删除失败');
        }
    };

    const handleClearAllConversations = async () => {
        try {
            await conversationApi.clear();
            setConversations([]);
            newConversation();
            MessageService.getInstance().showMessage('success', '清空成功');
        } catch (error) {
            MessageService.getInstance().showMessage('error', '清空失败');
        }
    };

    const handleNewConversation = () => {
        newConversation();
    };

    // render
    return (
        <AiAssistantWrapper>
            {contextHolder}
            <HeaderWrapper>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={handleNewConversation}
                        size="small"
                    >
                        新会话
                    </Button>
                </div>
                <Button
                    type="text"
                    icon={<HistoryOutlined />}
                    onClick={handleOpenHistory}
                    size="small"
                >
                    历史会话
                </Button>
            </HeaderWrapper>

            <ChatAreaWrapper ref={chatAreaRef}>
                {messages.length === 0 ? (
                    <Flex style={{ flexDirection: 'column', alignItems: 'center', padding: '40px', color: '#333333' }}>
                        <h1 style={{fontSize: '36px', letterSpacing:'2px', fontWeight: '600'}}>有什么我可以帮您的吗</h1>
                        <p style={{ fontSize: '14px', marginTop: '8px' }}>输入您的问题，我会尽力为您解答</p>
                        
                        <Flex style={{marginTop: '40px', width: '75%', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', alignItems: 'center'}}>
                            {faq.map((e, index)=> {
                                return <Button key={index} type="text" onClick={()=> handleSendFaq(e.question)}>{e.question}</Button>
                            })}
                        </Flex>
                    </Flex>
                ) : (
                    messages.map((message) => (
                        <MessageWrapper key={message.metadata.timestamp} isUser={message.metadata.messageType == 'USER'}>
                            <MessageBubble isUser={message.metadata.messageType == 'USER'}>
                                <MessageContent>
                                    <Markdown content={message.text} />
                                </MessageContent>
                            </MessageBubble>
                        </MessageWrapper>
                    ))
                )}
                {isSending && (
                    <MessageWrapper isUser={false}>
                        <MessageBubble isUser={false}>
                            <MessageLoading>
                                <span></span>
                                <span></span>
                                <span></span>
                            </MessageLoading>
                        </MessageBubble>
                    </MessageWrapper>
                )}
            </ChatAreaWrapper>

            <InputAreaWrapper>
                <InputWrapper>
                    <TextArea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="输入消息，按 Enter 发送，Shift + Enter 换行"
                        disabled={isSending}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSend}
                        disabled={!inputValue.trim() || isSending}
                        style={{width: '55px', height: '55px', borderRadius: '28px', fontSize: '22px'}}
                    >
                    </Button>
                </InputWrapper>
            </InputAreaWrapper>

            <Drawer
                title={
                    <Flex style={{alignItems: 'center', justifyContent: 'space-between'}}>
                        <span style={{ fontSize: '16px', fontWeight: 600 }}>历史会话</span>
                        <Popconfirm
                            title="删除的会话数据将无法找回，确定删除所有会话数据吗？"
                            onConfirm={handleClearAllConversations}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                disabled={loadingConversations || conversations.length === 0}
                            >
                                清空会话
                            </Button>
                        </Popconfirm>
                    </Flex>
                }
                placement="right"
                closable={false}
                onClose={handleCloseHistory}
                open={historyModalVisible}
                getContainer={false}
                style={{ position: 'absolute' }}
            >
                <HistoryModalWrapper>
                    {loadingConversations ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>
                    ) : conversations.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>暂无历史会话</div>
                    ) : (
                        <HistoryListWrapper>
                            {conversations.map((conv) => (
                                <HistoryItemWrapper key={conv.id}>
                                    <HistoryItemContent onClick={() => handleSelectConversation(conv)}>
                                        <HistoryItemTitle>{conv.name || '无标题会话'}</HistoryItemTitle>
                                        <HistoryItemTime>{moment(parseInt(conv.createdTime)).format('YYYY/MM/DD HH:mm')}</HistoryItemTime>
                                    </HistoryItemContent>
                                    <HistoryItemActions>
                                        <Dropdown trigger={['click']} menu={{
                                            items: [
                                                {
                                                    key: 'delete',
                                                    label: <Popconfirm
                                                        title="删除的会话数据将无法找回，确定删除此会话？"
                                                        onConfirm={(e) => {handleDeleteConversation(conv.id);}}
                                                        okText="确定"
                                                        cancelText="取消"
                                                    >
                                                        <Flex style={{alignItems: 'center'}}><DeleteOutlined style={{marginRight: '20px'}} /> 删除会话</Flex>
                                                    </Popconfirm>
                                                }
                                            ]}
                                        }>
                                            <MoreOutlined rotate={90} style={{fontSize: '20px'}} />
                                        </Dropdown>
                                    </HistoryItemActions>
                                </HistoryItemWrapper>
                            ))}
                        </HistoryListWrapper>
                    )}
                </HistoryModalWrapper>
            </Drawer>
            
        </AiAssistantWrapper>
    );
};

export default AiAssistant;